import { InfoOutlined, Layers, Schedule } from "@mui/icons-material";
import { Box, Grid2, Skeleton, Tooltip } from "@mui/material";
import Fade from "@mui/material/Fade";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import addDays from "date-fns/addDays";
import format from "date-fns/format";
import isSameDay from "date-fns/isSameDay";
import startOfWeek from "date-fns/startOfWeek";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import useSchedulerHolidays from "../../components/holidays/controller/use-scheduler-holidays";
import { getLocalDateKey } from "../../components/holidays/utils/holiday-dates";
import TooltipContent from "../../components/scheduler/ui/tooltip/tooltip-content";
import { useDateFnsLocale } from "../../utils/date-locale";
import { hasPermission } from "../../utils/helpers";
import { dayHeaderHeight, gutterWidth } from "./grid-scheduler/constants";
import useGridDaySchedulerController from "./grid-scheduler/controllers/use-grid-day-scheduler-controller";
import useGridSchedulerController from "./grid-scheduler/controllers/use-grid-scheduler-controller";
import MemoizedCurrentTimeIndicator from "./grid-scheduler/memoized-current-time-indicator";
import MemoizedDayColumn from "./grid-scheduler/memoized-day-column";
import MemoizedDayColumnHeader from "./grid-scheduler/memoized-day-column-header";
import { ShiftContainer } from "./grid-scheduler/shift-container";
import {
  DayHeader,
  SelectionBox,
  TimeLabel,
  TimeSlot,
  TimeSlotHeader,
} from "./grid-scheduler/styled-components";
import TimeSlotContainer from "./grid-scheduler/time-slot-container";
import {
  canSelectTime,
  getDayNumber,
  getLocalTimeZone,
  getShiftEffectiveStartDay,
  getUpdatedStackedShifts,
  hours
} from "./grid-scheduler/utils";

const WeekContextHeader = ({
  currentDate,
  weekStartDay,
  onDayClick,
}: {
  currentDate: Date;
  weekStartDay: string;
  onDayClick: (day: Date) => void;
}) => {
  const dateLocale = useDateFnsLocale();
  const weekStart = startOfWeek(currentDate, { weekStartsOn: getDayNumber(weekStartDay) });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const isCurrentDayToday = isSameDay(currentDate, new Date());
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: "16px",
        cursor: "pointer",
        padding: "4px 8px",
        borderRadius: "8px",
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: "rgba(47, 77, 139, 0.08)",
        },
      }}
      onClick={() => onDayClick(currentDate)}
    >
      {days.map((d, i) => {
        const isCurrentDay = isSameDay(d, currentDate);
        const isToday = isSameDay(d, new Date());
        if (isCurrentDay) {
          return (
            <Box
              key={i}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-end",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  lineHeight: 1.15,
                  fontWeight: isCurrentDayToday ? 600 : 400,
                  fontFamily: "Roboto",
                  color: isCurrentDayToday ? "#2F4D8B" : "rgba(0,0,0,0.6)",
                  marginRight: "0.5rem",
                }}
              >
                {format(d, "EEE", { locale: dateLocale })}
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  lineHeight: 1,
                  fontWeight: isCurrentDayToday ? 600 : 500,
                  fontFamily: "Roboto",
                  color: isCurrentDayToday ? "#2F4D8B" : "rgba(0,0,0,0.6)",
                }}
              >
                {format(d, "d")}
              </Typography>
            </Box>
          );
        }
        return (
          <Box
            key={i}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              gap: "3px",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                lineHeight: 1.15,
                fontWeight: isToday ? 600 : 400,
                fontFamily: "Roboto",
                color: isToday ? "rgba(47, 77, 139, 0.5)" : "rgba(0,0,0,0.25)",
              }}
            >
              {format(d, "EEE", { locale: dateLocale })}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.75rem",
                lineHeight: 1.15,
                fontWeight: isToday ? 600 : 400,
                fontFamily: "Roboto",
                color: isToday ? "rgba(47, 77, 139, 0.5)" : "rgba(0,0,0,0.25)",
              }}
            >
              {format(d, "d")}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default function MyScheduler({
  openPublishDraftShiftDialog,
  openShiftCancelDialog,
  openDeleteDraftShiftDialog,
  openCreateNewShiftDialog,
  openShiftDetailsDialog,
  setShiftIdForShiftDetailsDialog,
  openShiftAttendanceDialog,
  changeWeekAsyncStatus,
  changeDayAsyncStatus,
  height,
  filterAccordionHeight,
  isLoading,
}: any) {
  const { t } = useTranslation();
  const dateLocale = useDateFnsLocale();
  const weekControllerResult = useGridSchedulerController(height, openCreateNewShiftDialog);
  const dayControllerResult = useGridDaySchedulerController(height, openCreateNewShiftDialog);

  const isWeekView = weekControllerResult[0].isWeekView;
  // Use the appropriate controller based on view type
  const activeResult = isWeekView ? weekControllerResult : dayControllerResult;

  const [
    {
      anchorEl,
      dayColumnRefs,
      selectedShift,
      currentTime,
      timeSlotHeight,
      weekStartDay,
      startingDay,
      currentDate,
      todayPosition,
      pastWeekShifts,
      processedShifts,
      userRolesWithPermissions,
      canCreateOrEditShift,
      visible,
      isLayerPopoverOpen,
      layerEl,
      selectedHourDateInfo,
      hourPassingShifts,
      calendarRef,
      defaultCalendarScrollValue,
      recentShiftScrollValue,
      offsetStartTime,
    },
    {
      toggleVisibility,
      setVisible,
      generateSkeletonVisibility,
      openShiftPopover,
      closeLayerPopover,
      openLayerPopover,
      isLastDayOfWeek,
      setRecentShiftScrollValue,
      handleSelectionComplete,
      handleTimeSlotClick,
      handleDayHeaderClick,
    },
  ] = activeResult;

  const firstVisibleDay = useMemo(
    () =>
      !currentDate
        ? null
        : isWeekView
          ? startingDay
          : new Date(new Date(currentDate).setHours(0, 0, 0, 0)),
    [currentDate, isWeekView, startingDay]
  );
  const holidaysByDateKey = useSchedulerHolidays(
    firstVisibleDay,
    isWeekView ? 7 : 1
  );

  // Drag-to-create: pressing on an empty part of any day column starts a
  // selection; a plain click (no movement) creates a default-duration shift,
  // a drag creates a shift spanning the dragged range. Shift cards and gutter
  // controls stop propagation so their own clicks still work.
  const DRAG_THRESHOLD_PX = 5;
  const [selectionPreview, setSelectionPreview] = useState<{
    index: number;
    startY: number;
    endY: number;
  } | null>(null);
  const selectionRef = useRef<{
    index: number;
    dayIndex: number;
    startY: number;
    endY: number;
    moved: boolean;
  } | null>(null);

  const getColumnOffsetY = useCallback(
    (index: number, clientY: number) => {
      const el = dayColumnRefs.current[index];
      if (!el) return null;
      return clientY - el.getBoundingClientRect().top;
    },
    [dayColumnRefs]
  );

  // Keep the latest handlers/values reachable from the once-attached document
  // listeners without re-subscribing on every render.
  const dragHandlersRef = useRef({
    handleSelectionComplete,
    handleTimeSlotClick,
    getColumnOffsetY,
    timeSlotHeight,
  });
  dragHandlersRef.current = {
    handleSelectionComplete,
    handleTimeSlotClick,
    getColumnOffsetY,
    timeSlotHeight,
  };

  const handleColumnMouseDown = (
    event: React.MouseEvent,
    index: number,
    dayIndex: number
  ) => {
    if (event.button !== 0 || !canCreateOrEditShift) return;
    const rawY = getColumnOffsetY(index, event.clientY);
    if (rawY == null) return;
    const timeSpaceY = rawY - timeSlotHeight;
    // Ignore presses in the header/footer margins above hour 0 / below hour 23.
    if (timeSpaceY < 0 || timeSpaceY > 24 * timeSlotHeight) return;
    if (!canSelectTime(dayIndex, todayPosition, currentTime, timeSpaceY)) return;
    event.preventDefault();
    selectionRef.current = { index, dayIndex, startY: rawY, endY: rawY, moved: false };
    setSelectionPreview({ index, startY: rawY, endY: rawY });
  };

  useEffect(() => {
    const handleMove = (event: MouseEvent) => {
      const sel = selectionRef.current;
      if (!sel) return;
      const rawY = dragHandlersRef.current.getColumnOffsetY(sel.index, event.clientY);
      if (rawY == null) return;
      const moved = sel.moved || Math.abs(rawY - sel.startY) >= DRAG_THRESHOLD_PX;
      selectionRef.current = { ...sel, endY: rawY, moved };
      setSelectionPreview({ index: sel.index, startY: sel.startY, endY: rawY });
    };
    const handleUp = () => {
      const sel = selectionRef.current;
      if (!sel) return;
      selectionRef.current = null;
      setSelectionPreview(null);
      const {
        handleSelectionComplete: onComplete,
        handleTimeSlotClick: onClick,
        timeSlotHeight: slotHeight,
      } = dragHandlersRef.current;
      if (sel.moved) {
        onComplete(sel.dayIndex, sel.startY - slotHeight, sel.endY - slotHeight);
      } else {
        onClick(sel.dayIndex, sel.startY - slotHeight);
      }
    };
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };
  }, []);

  const getSelectionLabel = useCallback(
    (startY: number, endY: number) => {
      const minutesFromRawY = (rawY: number) => {
        const y = rawY - timeSlotHeight;
        const hour = Math.floor(y / timeSlotHeight);
        const minutes =
          Math.round(Math.floor((y % timeSlotHeight) / timeSlotHeight * 60) / 30) * 30;
        return hour * 60 + minutes;
      };
      const formatMinutes = (total: number) => {
        const base = new Date(startingDay);
        base.setHours(0, total, 0, 0);
        return format(base, "h:mm a");
      };
      const a = minutesFromRawY(startY);
      const b = minutesFromRawY(endY);
      return `${formatMinutes(Math.min(a, b))} - ${formatMinutes(Math.max(a, b))}`;
    },
    [startingDay, timeSlotHeight]
  );

  const showSkeleton = useMemo(() => {
    return (
      changeWeekAsyncStatus === "pending" ||
      !currentDate ||
      changeDayAsyncStatus === "pending" ||
      isLoading
    );
  }, [changeDayAsyncStatus, changeWeekAsyncStatus, currentDate, isLoading]);

  // Memoized Skeleton Header loader for performance improvement
  const memoizedDayHeadersWithSkeleton = useMemo(() => {
    return Array.from({ length: isWeekView ? 7 : 1 }).map((_, index) => {
      return (
        <MemoizedDayColumnHeader
          key={`day-header-skeleton-${index}`}
          day={addDays(new Date(), 1)}
          width={"100%"}
          offsetStartTime={offsetStartTime}
        >
          <DayHeader>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "flex-end",
              }}
            >
              <Skeleton
                variant="rounded"
                width={30}
                height={14}
                sx={{
                  backgroundColor: "#F5F5F5",
                  marginRight: 1,
                }}
              />
              <Skeleton
                variant="rounded"
                width={40}
                height={23}
                sx={{ backgroundColor: "#F5F5F5"}}
              />
            </Box>
          </DayHeader>
        </MemoizedDayColumnHeader>
      );
    });
  }, [isWeekView, offsetStartTime]);

  // Memoized Skeleton Time slots loader for performance improvement
  const memoizedTimeSlotsWithSkeleton = useMemo(() => {
    return Array.from({ length: isWeekView ? 7 : 1 }).map((_, dayIndex) => {
      const day = isWeekView ? addDays(new Date(), 1) : new Date();
      const skeletonVisibility = generateSkeletonVisibility(
        hours(offsetStartTime).length,
        dayIndex
      );
      return (
        <MemoizedDayColumn
          key={dayIndex}
          day={day}
          timeSlotHeight={timeSlotHeight}
          width={"100%"}
        >
          {hours(offsetStartTime).map((_, hourIndex) => (
            <TimeSlot height={timeSlotHeight} key={hourIndex}>
              {skeletonVisibility[hourIndex] ? (
                <Skeleton
                  key={`shift-skeleton--${dayIndex}-${hourIndex}`}
                  variant="rounded"
                  width="100%"
                  animation="wave"
                  height={timeSlotHeight * 2}
                  sx={{
                    backgroundColor: "#F5F5F5",
                    marginLeft: "12px",
                    marginRight: "24px",
                  }}
                />
              ) : null}
            </TimeSlot>
          ))}
        </MemoizedDayColumn>
      );
    });
  }, [generateSkeletonVisibility, isWeekView, offsetStartTime, timeSlotHeight]);

  useEffect(() => {
    if (calendarRef.current) {
      calendarRef.current.scrollTo({
        top: defaultCalendarScrollValue,
        behavior: "smooth",
      });
    }
  }, [calendarRef, defaultCalendarScrollValue, currentDate]);

  useEffect(() => {
    try {
      if (calendarRef.current && recentShiftScrollValue !== 0) {
        calendarRef.current.scrollTo({
          top: recentShiftScrollValue,
          behavior: "smooth",
        });
        setRecentShiftScrollValue(0);
      }
    } catch (e) {
      // Do nothing
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarRef, recentShiftScrollValue]);

  return (
    <>
      <Box
        ref={calendarRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: `${height}px`,
          minHeight: `${filterAccordionHeight}px`,
          maxHeight: `${height}px`,
          overflow: "auto",
          flex: 1,
          userSelect: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flex: 1,
            alignItems: "flex-start",
          }}
        >
          {/* Time Labels and Indicators */}
          <Box
            sx={{
              minWidth: "80px",
              maxWidth: "80px",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                position: "sticky",
                top: 0,
                height: `${dayHeaderHeight}px`,
                minHeight: `${dayHeaderHeight}px`,
                backgroundColor: "rgba(255,255,255)",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                zIndex: 1001,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  lineHeight: 1.66,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  color: "rgba(0,0,0,0.3)",
                  textAlign: "right",
                  paddingBottom: "12px",
                }}
              >
                {getLocalTimeZone()}
              </Typography>
            </Box>
            <TimeSlotHeader
              height={timeSlotHeight}
              key={"time-slot-header-first"}
            />
            {hours(offsetStartTime).map((hour, index) => (
              <TimeSlotHeader
                height={timeSlotHeight}
                key={`time-slot-header-${index}`}
              >
                <TimeLabel>
                  <Box
                    sx={{
                      width: "100%",
                      marginRight: "8px",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    {/* 12 AM require tooltip and info icon */}
                    {hour === "12 AM" && offsetStartTime !== 0 ? (
                      <Tooltip
                        title={t("flex-scheduler_calendar-date-changed")}
                        placement="left"
                      >
                        <Box sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "fit-content",
                          borderRadius: "4px",
                          padding: "1px 4px",
                          backgroundColor: "rgba(0,0,0,0.05)",
                          marginTop: -1,
                          marginRight: 0.5,
                          gap: "2px",
                        }}>
                          <InfoOutlined sx={{
                            fontSize: "0.7rem",
                            lineHeight: 1.66,
                            color: "rgba(0,0,0,0.6)",
                          }}/>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              lineHeight: 1.66,
                              fontWeight: 400,
                              fontFamily: "Roboto",
                              color: "rgba(0,0,0,0.6)",
                              textAlign: "right",
                            }}
                          >
                            {hour}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          lineHeight: 1.66,
                          fontWeight: 400,
                          fontFamily: "Roboto",
                          marginTop: -1,
                          marginRight: 1,
                          color: "rgba(0,0,0,0.6)",
                          textAlign: "right",
                        }}
                      >
                        {hour}
                      </Typography>
                    )}
                  </Box>
                </TimeLabel>
              </TimeSlotHeader>
            ))}
            <TimeSlotHeader
              height={timeSlotHeight}
              key={"time-slot-header-last"}
            >
              <TimeLabel>
                <Box
                  sx={{
                    width: "100%",
                    marginRight: "8px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      lineHeight: 1.66,
                      fontWeight: 400,
                      fontFamily: "Roboto",
                      marginTop: -1,
                      marginRight: 1,
                      color: "rgba(0,0,0,0.6)",
                      textAlign: "right",
                    }}
                  >
                    {(() => {
                      const offsetHours = offsetStartTime ? Math.floor(offsetStartTime / (1000 * 60 * 60)) : 0;
                      // Calculate the last hour (after 24 hours of offset + 1 extra hour)
                      const lastHour = (offsetHours + 24) % 24;
                      const hour = lastHour % 12 === 0 ? 12 : lastHour % 12;
                      const period = lastHour < 12 ? "AM" : "PM";
                      return `${hour} ${period}`;
                    })()}
                  </Typography>
                </Box>
              </TimeLabel>
            </TimeSlotHeader>
          </Box>

          {/* Day Columns */}
          <Grid2
            container
            width={"100%"}
            sx={{
              flexGrow: 1,
            }}
          >
            {/* Day headers */}
            <Grid2
              container
              width={"100%"}
              sx={{
                position: "sticky",
                top: 0,
                height: `${dayHeaderHeight}px`,
                minHeight: `${dayHeaderHeight}px`,
                backgroundColor: "rgba(255,255,255)",
                boxShadow: "0px 2px 4px -2px rgba(0, 0, 0, 0.2)",
                flexWrap: "nowrap",
                zIndex: 1000,
              }}
            >
              {showSkeleton
                ? memoizedDayHeadersWithSkeleton
                : currentDate &&
                Array.from({ length: isWeekView ? 7 : 1 }).map((_, index) => {
                  const day = isWeekView
                    ? addDays(startingDay, index)
                    : new Date(new Date(currentDate).setHours(0, 0, 0, 0));
                  return (
                    <MemoizedDayColumnHeader
                      key={index}
                      day={day}
                      width={"100%"}
                      offsetStartTime={offsetStartTime}
                      holidayOccurrences={holidaysByDateKey[getLocalDateKey(day)]}
                    >
                      <DayHeader>
                        {isWeekView ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "center",
                              alignItems: "flex-end",
                              cursor: "pointer",
                              borderRadius: "6px",
                              padding: "4px 8px",
                              transition: "background-color 0.15s ease",
                              "&:hover": {
                                backgroundColor: "rgba(47, 77, 139, 0.08)",
                              },
                            }}
                            onClick={() => handleDayHeaderClick(day)}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.875rem",
                                lineHeight: 1.15,
                                fontWeight: isSameDay(day, new Date())
                                  ? 600
                                  : 400,
                                fontFamily: "Roboto",
                                color: isSameDay(day, new Date())
                                  ? "#2F4D8B"
                                  : "rgba(0,0,0,0.6)",
                                marginRight: "0.5rem"
                              }}
                            >
                              {format(day, "EEE", { locale: dateLocale })}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "1.25rem",
                                lineHeight: 1,
                                fontWeight: isSameDay(day, new Date())
                                  ? 600
                                  : 500,
                                fontFamily: "Roboto",
                                color: isSameDay(day, new Date())
                                  ? "#2F4D8B"
                                  : "rgba(0,0,0,0.6)",
                              }}
                            >
                              {format(day, "d")}
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            "& .day-header-date": {
                              display: "flex",
                            },
                            "& .day-header-week-context": {
                              display: "none",
                            },
                            "&:hover .day-header-date": {
                              display: "none",
                            },
                            "&:hover .day-header-week-context": {
                              display: "flex",
                            },
                          }}>
                            <Box className="day-header-date" sx={{
                              flexDirection: "row",
                              justifyContent: "center",
                              alignItems: "flex-end",
                            }}>
                              <Typography
                                sx={{
                                  fontSize: "0.875rem",
                                  lineHeight: 1.15,
                                  fontWeight: isSameDay(day, new Date())
                                    ? 600
                                    : 400,
                                  fontFamily: "Roboto",
                                  color: isSameDay(day, new Date())
                                    ? "#2F4D8B"
                                    : "rgba(0,0,0,0.6)",
                                  marginRight: "0.5rem"
                                }}
                              >
                                {format(day, "EEE", { locale: dateLocale })}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "1.25rem",
                                  lineHeight: 1,
                                  fontWeight: isSameDay(day, new Date())
                                    ? 600
                                    : 500,
                                  fontFamily: "Roboto",
                                  color: isSameDay(day, new Date())
                                    ? "#2F4D8B"
                                    : "rgba(0,0,0,0.6)",
                                }}
                              >
                                {format(day, "d")}
                              </Typography>
                            </Box>
                            <Box className="day-header-week-context">
                              <WeekContextHeader
                                currentDate={day}
                                weekStartDay={weekStartDay}
                                onDayClick={handleDayHeaderClick}
                              />
                            </Box>
                          </Box>
                        )}
                      </DayHeader>
                    </MemoizedDayColumnHeader>
                  );
                })}
            </Grid2>
            <Grid2
              container
              width={"100%"}
              sx={{
                overflow: "hidden",
                flexWrap: "nowrap",
                backgroundColor: "#f1f1f139",
              }}
            >
              {/* Time slots */}
              {showSkeleton
                ? memoizedTimeSlotsWithSkeleton
                : Array.from({ length: isWeekView ? 7 : 1 }).map((_, index) => {
                  const day = isWeekView
                    ? addDays(startingDay, index)
                    : currentDate
                      ? new Date(currentDate)
                      : new Date();
                  const dayIndex = isWeekView
                    ? index
                    : (day.getDay() + 7 - getDayNumber(weekStartDay)) % 7;
                  return (
                    <Box
                      key={dayIndex}
                      ref={(el) => (dayColumnRefs.current[index] = el)}
                      onMouseDown={(event) =>
                        handleColumnMouseDown(event, index, dayIndex)
                      }
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                        position: "relative",
                      }}>
                      {selectionPreview?.index === index && (
                        <SelectionBox
                          sx={{
                            top: `${Math.min(
                              selectionPreview.startY,
                              selectionPreview.endY
                            )}px`,
                            height: `${Math.abs(
                              selectionPreview.endY - selectionPreview.startY
                            )}px`,
                            width: "calc(100% - 4px)",
                            zIndex: 1000,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Roboto",
                              fontSize: "14px",
                              lineHeight: "20px",
                              color: "#FFFFFF",
                              fontWeight: 500,
                            }}
                          >
                            {getSelectionLabel(
                              selectionPreview.startY,
                              selectionPreview.endY
                            )}
                          </Typography>
                        </SelectionBox>
                      )}
                    <MemoizedDayColumn
                      key={dayIndex}
                      day={day}
                      timeSlotHeight={timeSlotHeight}
                      width={"100%"}
                    >
                      {dayIndex === todayPosition && (
                        <MemoizedCurrentTimeIndicator
                          currentTime={currentTime}
                        />
                      )}
                      {hours(offsetStartTime).map((hour, hourIndex) => {
                        const offsetHours = Math.floor(offsetStartTime / (1000 * 60 * 60));
                        const adjustedHourIndex = (hourIndex + offsetHours) % 24;
                        // all shifts passing, ending or originating on this hour
                        const allPassingShiftsPre = processedShifts?.filter(
                          (shift: any) => {
                            const startDate = new Date(
                              shift.startDateTime
                            ).getTime();
                            const endDate = new Date(
                              shift.endDateTime
                            ).getTime();
                            const baseZoneStart = new Date(day).setHours(
                              hourIndex,
                              0,
                              0,
                              0
                            );
                            const baseZoneEnd = new Date(day).setHours(
                              hourIndex + 1,
                              0,
                              0,
                              0
                            );
                            const zoneStartDate = baseZoneStart + offsetStartTime;
                            const zoneEndDate = baseZoneEnd + offsetStartTime;

                            if (shift.shiftEndHourIndex !== -1) {
                              return (
                                (startDate >= zoneStartDate &&
                                  startDate <= zoneEndDate) ||
                                (endDate >= zoneStartDate &&
                                  endDate <= zoneEndDate) ||
                                (startDate <= zoneStartDate &&
                                  endDate >= zoneEndDate)
                              );
                            } else {
                              return (
                                (startDate >= zoneStartDate &&
                                  startDate < zoneEndDate) ||
                                (endDate > zoneStartDate &&
                                  endDate <= zoneEndDate) ||
                                (startDate <= zoneStartDate &&
                                  endDate >= zoneEndDate)
                              );
                            }
                          }
                        ) ?? [];

                        // Sort all passing shifts by start time
                        const allPassingShifts = allPassingShiftsPre?.sort(
                          (a: any, b: any) => {
                            const aStart = new Date(a.startDateTime).getTime();
                            const bStart = new Date(b.startDateTime).getTime();
                            return aStart - bStart;
                          }
                        ) ?? [];

                        const validShifts = processedShifts?.filter(
                          (shift: any) => {
                            const isPastWeekShifts = pastWeekShifts.some(
                              (s: { id: any }) => s.id === shift.id
                            );
                            const fromNextDay = shift.shiftDayIndex - 1 === dayIndex &&
                              adjustedHourIndex === offsetHours;
                            const fromPreviousDay =(shift.shiftEndHourIndex !== -1
                              && shift.shiftEndHourIndex === adjustedHourIndex
                              && shift.shiftNextDayIndex === dayIndex
                            );
                            return (
                              (!isPastWeekShifts
                                && shift.shiftHourIndex === adjustedHourIndex
                                && (shift.shiftDayIndex === dayIndex
                                || fromNextDay)
                              ) || fromPreviousDay
                            );
                          }
                        );

                        const shiftsToRender = validShifts.map(
                          (shift: any, shiftIndex: number) => {
                            const ignoreThisHourSlotForInfoAtBottom = offsetHours - 1 >= 0 ?
                              offsetHours - 1 : 23;
                            const showInfoAtBottom = shift.shiftEndHourIndex !== -1 &&
                              shift.shiftNextDayIndex === dayIndex &&
                              getShiftEffectiveStartDay(new Date(
                                shift.startDateTime
                              )).getHours() !== ignoreThisHourSlotForInfoAtBottom;
                            // To handle last hour overlapping shifts
                            const fromNextDay = shift.shiftDayIndex - 1 === dayIndex &&
                              adjustedHourIndex === offsetHours;
                            const fromPreviousDay =(shift.shiftEndHourIndex !== -1
                              && shift.shiftEndHourIndex === adjustedHourIndex
                              && shift.shiftNextDayIndex === dayIndex
                            );
                            return (
                              <Box
                                key={`shift-${shift.id}-${shiftIndex}`}
                                sx={{
                                  position: "absolute",
                                  top: fromNextDay ?
                                    `${24*timeSlotHeight}px`
                                    : fromPreviousDay
                                      ? "auto"
                                      : `${shift.shiftTop}%`,
                                  bottom:
                                    fromPreviousDay
                                      ? `${shift.shiftBottom}%`
                                      : "auto",
                                  height: `${shift.shiftHeight}px`,
                                  left: isWeekView ?
                                    (() => {
                                      return shift.shiftWidthPercent === 50 &&
                                        shift.changedMarginOfShift === 50
                                          ? `calc(${
                                            shift.shiftIndexInOverlapping > 0
                                              ? shift.changedMarginOfShift
                                              : 0
                                          }% - ${gutterWidth / 4}px)`
                                          : `calc(${
                                            shift.shiftIndexInOverlapping > 0
                                              ? shift.changedMarginOfShift
                                              : 0
                                          }% + ${gutterWidth / 2}px)`;
                                    })
                                      :
                                    (() => {
                                    const equalThreshold = 14;
                                    const isEqualLayout = shift.overlappingShiftsCount > 1
                                      && shift.overlappingShiftsCount <= equalThreshold;
                                    if (isEqualLayout) {
                                      return `calc(12px + (100% - 36px) * ${shift.shiftIndexInOverlapping} / ${shift.overlappingShiftsCount})`;
                                    }
                                    return `calc(${shift.shiftIndexInOverlapping > 0 ? shift.changedMarginOfShift : 0}% + ${gutterWidth / 2}px)`;
                                  })(),
                                  width: isWeekView ?
                                    (() => {
                                      return shift.shiftWidthPercent === 50
                                          ? `calc(${
                                            shift.shiftWidthPercent
                                          }% - ${(gutterWidth * 3) / 4}px)`
                                          : `calc(${
                                            shift.shiftWidthPercent
                                          }% - ${
                                            gutterWidth / 2
                                          }px - ${gutterWidth}px)`
                                    })
                                    :
                                    (() => {
                                    const equalThreshold = 14;
                                    const isEqualLayout = shift.overlappingShiftsCount > 1
                                      && shift.overlappingShiftsCount <= equalThreshold;
                                    if (isEqualLayout) {
                                      return `calc((100% - 36px) / ${shift.overlappingShiftsCount})`;
                                    }
                                    const maxWidth = `calc(${100 - shift.changedMarginOfShift}% - ${gutterWidth + gutterWidth / 2}px)`;
                                    const desiredWidth = `calc(${shift.shiftWidthPercent}% - ${gutterWidth / 2}px - ${gutterWidth}px)`;
                                    return `min(${desiredWidth}, ${maxWidth})`;
                                  })(), // offset for last row shift card while overlapping
                                  cursor: "pointer",
                                  zIndex: (() => {
                                    // Keep all shift zIndex values under 1000, ordered by shiftStartTime within a day
                                    // Shifts from previous days appear behind same-day shifts; next-day shifts appear on top
                                    if (fromPreviousDay) return 1;
                                    if (fromNextDay) return 999;
                                    const shiftDate = new Date(shift.startDateTime);
                                    const minutesOfDay = shiftDate.getHours() * 60 + shiftDate.getMinutes();
                                    // Normalize to 2-998 range (1 reserved for cross-day shifts, 999+ for UI chrome)
                                    return Math.max(2, Math.min(998, Math.floor((minutesOfDay / 1440) * 997) + 2));
                                  })(),
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  anchorEl.current = e.currentTarget;
                                  selectedShift.current = {
                                    ...shift,
                                    dayIndex: dayIndex,
                                  };

                                  if (!visible) {
                                    setVisible(true);
                                  }
                                }}
                              >
                                <ShiftContainer
                                  data={shift}
                                  infoAtBottom={showInfoAtBottom}
                                  isSelected={
                                    shift.id === selectedShift.current?.id
                                  }
                                  timeSlotHeight={timeSlotHeight}
                                  offsetHours={offsetHours}
                                />
                              </Box>
                            );
                          }
                        );
                        return (
                          <TimeSlotContainer
                            isDragging={selectionPreview !== null}
                            openCreateNewShiftDialog={
                              openCreateNewShiftDialog
                            }
                            canCreateOrEditShift={
                              hasPermission(userRolesWithPermissions, "FLEX_SCHEDULER.CREATE_SHIFT") &&
                              canCreateOrEditShift
                            }
                            dayIndex={dayIndex}
                            todayPosition={todayPosition}
                            hourIndex={hourIndex}
                            day={day}
                            timeSlotHeight={timeSlotHeight}
                            allPassingShifts={allPassingShifts}
                            openLayerPopover={openLayerPopover}
                            selectedHourDateInfo={selectedHourDateInfo}
                            key={hourIndex}
                            offsetStartTime={offsetStartTime}
                          >
                            {shiftsToRender ? shiftsToRender : null}
                          </TimeSlotContainer>
                        );
                      })}
                    </MemoizedDayColumn>
                    </Box>
                  );
                })}
            </Grid2>
          </Grid2>
        </Box>
      </Box>
      {anchorEl.current && selectedShift.current && (
        <Popover
          open={visible}
          anchorEl={anchorEl.current}
          onClose={() => {
            selectedShift.current = null;
            setVisible(false);
          }}
          slots={{
            transition: Fade,
          }}
        >
          <TooltipContent
            shiftData={selectedShift.current}
            setShiftIdForShiftDetailsDialog={setShiftIdForShiftDetailsDialog}
            openPublishDraftShiftDialog={openPublishDraftShiftDialog}
            openShiftCancelDialog={openShiftCancelDialog}
            openShiftDetailsDialog={openShiftDetailsDialog}
            openDeleteDraftShiftDialog={openDeleteDraftShiftDialog}
            openShiftAttendanceDialog={openShiftAttendanceDialog}
            toggleVisibility={toggleVisibility}
            visible={visible}
          />
        </Popover>
      )}
      {layerEl && (
        <Popover
          open={isLayerPopoverOpen}
          anchorEl={layerEl}
          onClose={closeLayerPopover}
          anchorOrigin={{
            vertical: "center",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "240px",
              maxHeight: "560px",
              overflow: "scroll",
              backgroundColor: "#fff",
            }}
          >
            <Box
              sx={{
                position: "sticky",
                top: 0,
                left: 0,
                width: "100%",
                backgroundColor: "#fff",
                zIndex: 999,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  backgroundColor: "#2F4D8029",
                  padding: "12px 20px",
                  gap: "4px"
                }}
              >
                <Box sx={{
                  display: "flex",
                  width: "100%",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}>
                  <Schedule sx={{
                    fontSize: "18px",
                    fontWeight: 500,
                  }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      lineHeight: "20px",
                      fontFamily: "Roboto",
                      fontWeight: 500,
                      color: "#000",
                      marginLeft: "8px"
                    }}
                  >
                    {`${selectedHourDateInfo?.hourText}, ${selectedHourDateInfo?.dayText} ${selectedHourDateInfo?.dateText}`}
                  </Typography>
                </Box>
                <Box sx={{
                  display: "flex",
                  width: "100%",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}>
                  <Layers sx={{
                    fontSize: "18px",
                    fontWeight: 500,
                  }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      lineHeight: "20px",
                      fontFamily: "Roboto",
                      fontWeight: 500,
                      color: "#000",
                      marginLeft: "8px"
                    }}
                  >
                    {t("flex-scheduler_stacked-shifts-count", {
                      count: getUpdatedStackedShifts(hourPassingShifts, processedShifts)?.length ??
                        selectedHourDateInfo.stackedShiftsText,
                    })}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                paddingLeft: "12px",
                paddingRight: "12px",
              }}
            >
              {getUpdatedStackedShifts(hourPassingShifts, processedShifts).map((shift: any, index: number) => (
                <Box
                  key={`hour-passing-shift-${index}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    width: "100%",
                    paddingTop: index === 0 ? "12px" : "6px",
                    paddingBottom:
                      index === getUpdatedStackedShifts(hourPassingShifts, processedShifts).length - 1 ? "12px" : "6px",
                  }}
                  onClick={(e) => {
                    openShiftPopover(e.currentTarget, shift);
                  }}
                >
                  <ShiftContainer
                    data={shift}
                    isLayer={true}
                    isSelected={selectedShift?.current?.id === shift?.id}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Popover>
      )}
    </>
  );
}